import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";
import VolunteerRegistrationModal from "../../components/VolunteerRegistrationModal";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { errorAlert } from "../../utils/alert";
import { FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { getVolunteerRegistrationLabel, isVolunteerRegistrationOpen } from "../../utils/eventVolunteer";

const formatDate = (raw) => {
  if (!raw) return "Date TBD";
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
};

const formatEventDateRange = (startRaw, endRaw) => {
  const start = startRaw || null;
  const end = endRaw || startRaw || null;
  if (!start && !end) return "Date TBD";
  if (!start) return formatDate(end);
  if (!end || end === start) return formatDate(start);
  return `${formatDate(start)} to ${formatDate(end)}`;
};

const parseDateForSort = (ev) => {
  const val = ev?.date || ev?.event_date || ev?.event_end_date || ev?.end_date || null;
  if (!val) return new Date(0);
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

const Volunteer = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [volunteerEvent, setVolunteerEvent] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await API.fetchEvents();
        const eventList = res && Array.isArray(res.data) ? res.data : [];
        setEvents(eventList);
      } catch (err) {
        console.error("Error loading volunteer events:", err);
        setError("Failed to load volunteer opportunities. Please try again later.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!error) return;
    errorAlert("Failed to load volunteer opportunities", error);
  }, [error]);

  const volunteerEligibleEvents = useMemo(() => {
    const rows = (Array.isArray(events) ? events : [])
      .filter((event) => isVolunteerRegistrationOpen(event))
      .sort((a, b) => parseDateForSort(b) - parseDateForSort(a));
    return rows;
  }, [events]);

  return (
    <div className="bg-slate-50 min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-blue-900 text-white py-20 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Volunteer With Us</h1>
          <p className="text-xl opacity-90">
            Join our mission to empower Para athletes and make a difference in the community.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Make an Impact</h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              Volunteering with the Para Sports Association of Andhra Pradesh gives you the opportunity
              to contribute to a meaningful cause while developing valuable skills and creating lasting
              connections with like-minded individuals.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Ways to Volunteer</h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Event coordination and management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Coaching and training assistance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Fundraising and sponsorship coordination</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Administrative support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Media and communications</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Benefits</h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1">•</span>
                    <span>Personal growth and skill development</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1">•</span>
                    <span>Networking opportunities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1">•</span>
                    <span>Certificate of appreciation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1">•</span>
                    <span>Priority access to events</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 mt-1">•</span>
                    <span>Satisfaction of making a difference</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Get Involved</h3>
              <p className="text-slate-600 mb-4">
                Interested in volunteering? Choose any event listed below to submit your volunteer
                or escort registration directly, or contact us to learn more about upcoming opportunities.
              </p>
              <div className="text-slate-700">
                <p><strong>Email:</strong> volunteer@parasportsap.org</p>
                <p><strong>Phone:</strong> +91-XXXXXXXXXX</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-slate-200">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                Current Volunteer Opportunities
              </h2>
              <p className="mt-3 text-slate-600 text-lg">
                Apply as a volunteer or escort for any event that is open now or currently in progress.
              </p>
            </div>

            {loading ? (
              <div className="text-center text-slate-500 mt-10">Loading volunteer opportunities...</div>
            ) : volunteerEligibleEvents.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 mt-10">
                {volunteerEligibleEvents.map((event) => {
                  const eventDate = event.date || event.event_date || null;
                  const eventEndDate = event.event_end_date || eventDate;
                  const imgSrc = resolveAssetUrl(event.image, {
                    folder: "events",
                    defaultPath: "/images/image-fallback.svg",
                  });

                  return (
                    <article
                      key={event.id}
                      className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm hover:shadow-lg transition-shadow"
                    >
                      <div className="relative h-52">
                        <img
                          src={imgSrc}
                          alt={event.title || "Volunteer event"}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/images/image-fallback.svg";
                          }}
                        />
                        <div className="absolute top-4 left-4 px-3 py-1 text-xs font-bold rounded-full bg-emerald-100 text-emerald-800">
                          {getVolunteerRegistrationLabel(event)}
                        </div>
                      </div>

                      <div className="p-5">
                        <h3 className="text-xl font-bold text-slate-900">{event.title}</h3>
                        <div className="mt-3 space-y-2 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt />
                            <span>{formatEventDateRange(eventDate, eventEndDate)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaMapMarkerAlt />
                            <span>{event.location || "TBD"}</span>
                          </div>
                        </div>
                        <p className="mt-4 text-slate-600 text-sm line-clamp-3">
                          {event.description || "No description provided."}
                        </p>
                        <button
                          onClick={() => setVolunteerEvent(event)}
                          className="mt-5 w-full rounded-xl bg-emerald-600 text-white font-semibold px-4 py-3 hover:bg-emerald-700 transition-colors"
                        >
                          Apply as Volunteer / Escort
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-500">
                No events are currently accepting volunteer or escort registrations.
              </div>
            )}
          </div>
        </div>
      </section>

      <PageSectionsRenderer pageKey="volunteer" className="pt-0" />

      <Footer />

      <VolunteerRegistrationModal
        event={volunteerEvent}
        onClose={() => setVolunteerEvent(null)}
      />
    </div>
  );
};

export default Volunteer;
